import api from '@/services/api';
import { create } from 'zustand';

interface Folder {
  id: string;
  user_email: string;
  name: string;
  uidnext: number;
  uidvalidity: number;
  type: string;
  category: string;
  total: number;
  new: number;
  unseen: number;
  highestmodseq: string | null;
  last_synced: string | null;
  created_at: string | null;
}

interface FolderStore {
  currentFolder: string;
  folders: Folder[];
  error: string | null;
  loading: boolean;
  setCurrentFolder: (folder: string) => void;
  setFolders: (folders: Folder[]) => void;
  fetchFolders: () => Promise<void>;
  updateFolderCounts: (folderName: string, newCount: number, totalCount: number, unseenCount?: number) => void;
  decrementUnreadCount: (folderName: string) => void;
}

const LOCAL_KEY = 'email_folders';

const defaultFolders: Folder[] = [
  {
    id: '',
    user_email: '',
    name: 'INBOX',
    uidnext: 1,
    uidvalidity: 1,
    type: 'system',
    category: 'system',
    total: 0,
    new: 0,
    unseen: 0,
    highestmodseq: null,
    last_synced: null,
    created_at: null
  },
  {
    id: '',
    user_email: '',
    name: 'Sent',
    uidnext: 1,
    uidvalidity: 1,
    type: 'system',
    category: 'system',
    total: 0,
    new: 0,
    unseen: 0,
    highestmodseq: null,
    last_synced: null,
    created_at: null
  },
  {
    id: '',
    user_email: '',
    name: 'Drafts',
    uidnext: 1,
    uidvalidity: 1,
    type: 'system',
    category: 'system',
    total: 0,
    new: 0,
    unseen: 0,
    highestmodseq: null,
    last_synced: null,
    created_at: null
  },
  {
    id: '',
    user_email: '',
    name: 'Trash',
    uidnext: 1,
    uidvalidity: 1,
    type: 'system',
    category: 'system',
    total: 0,
    new: 0,
    unseen: 0,
    highestmodseq: null,
    last_synced: null,
    created_at: null
  },
  {
    id: '',
    user_email: '',
    name: 'Junk',
    uidnext: 1,
    uidvalidity: 1,
    type: 'system',
    category: 'system',
    total: 0,
    new: 0,
    unseen: 0,
    highestmodseq: null,
    last_synced: null,
    created_at: null
  }
];

export const useFolderStore = create<FolderStore>((set) => ({
  currentFolder: 'INBOX',
  folders: JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]') || defaultFolders,
  error: null,
  loading: false,
  
  setCurrentFolder: (folder: string) => {
    // Only update if the folder actually changes
    set(state => {
      if (state.currentFolder !== folder) {
        return { currentFolder: folder };
      }
      return state;
    });
  },
  
  setFolders: (folders: Folder[]) => {
    set({ folders, error: null });
    localStorage.setItem(LOCAL_KEY, JSON.stringify(folders));
  },
  
  fetchFolders: async () => {
    try {
      set({ loading: true, error: null });
      const res = await api.get('/api/folders');
      const data = res.data;
      
      // Merge system folders with custom folders
      const mergedFolders = defaultFolders.map(defaultFolder => {
        const apiFolder = data.find((f: Folder) => f.name === defaultFolder.name);
        return apiFolder || defaultFolder;
      });

      // Add any additional custom folders from the API
      const customFolders = data.filter((f: Folder) => 
        !defaultFolders.some(df => df.name === f.name)
      );

      const allFolders = [...mergedFolders, ...customFolders];
      
      // Update state and localStorage
      set({ folders: allFolders, error: null, loading: false });
      localStorage.setItem(LOCAL_KEY, JSON.stringify(allFolders));
    } catch (err: any) {
      if (err.response && err.response.status === 500) {
        // Keep using cached folders from localStorage
        const cachedFolders = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
        if (cachedFolders.length > 0) {
          set({ folders: cachedFolders, loading: false });
        } else {
          set({ folders: defaultFolders, loading: false });
        }
      } else if (err.response && err.response.data && typeof err.response.data === 'string') {
        set({ error: err.response.data, loading: false });
      } else {
        set({ error: err.message || 'Unknown error', loading: false });
      }
    }
  },
  updateFolderCounts: (folderName: string, newCount: number, totalCount: number, unseenCount?: number) => {
    // console.log('🔄 Updating folder counts in store:', { folderName, newCount, totalCount, unseenCount });
    set((state) => {
      const updatedFolders = state.folders.map(folder => {
        if (folder.name === folderName) {
          // console.log('📊 Folder count update:', { 
          //   folder: folder.name, 
          //   newCount, 
          //   totalCount, 
          //   unseenCount 
          // });
          
          return {
            ...folder,
            new: newCount,
            total: totalCount,
            unseen: unseenCount ?? folder.unseen
          };
        }
        return folder;
      });
      
      // Update localStorage
      localStorage.setItem(LOCAL_KEY, JSON.stringify(updatedFolders));
      
      return { folders: updatedFolders };
    });
  },
  decrementUnreadCount: (folderName: string) => {
    set((state) => {
      const updatedFolders = state.folders.map(folder => {
        if (folder.name === folderName) {
          const newUnseenCount = Math.max(0, folder.unseen - 1);
          const newNewCount = Math.max(0, folder.new - 1);
          
          // console.log('📊 Decrementing unread counts for folder:', { 
          //   folder: folder.name, 
          //   oldUnseen: folder.unseen,
          //   newUnseen: newUnseenCount,
          //   oldNew: folder.new,
          //   newNew: newNewCount
          // });
          
          return {
            ...folder,
            unseen: newUnseenCount,
            new: newNewCount
          };
        }
        return folder;
      });
      
      // Update localStorage
      localStorage.setItem(LOCAL_KEY, JSON.stringify(updatedFolders));
      
      return { folders: updatedFolders };
    });
  }
})); 